import React, { useState, useEffect, useMemo } from 'react';
import { Tag, Typography, Space, Popover, Row, Col, Table, message, Drawer, Input, Select, Spin } from 'antd';
import useApiFetch from '../../hooks/useApiFetch';
import FeatherIcon from 'feather-icons-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/page-headers/page-headers';
import { Main, CardToolbox, TableWrapper, ProjectSorting } from '../styled';
import { Cards } from '../../components/cards/frame/cards-frame';
import qs from 'qs';
import { useAuth } from '../../context/auth.context';
import TableColumnManager from '../../components/TableColumnManager';
import { getItem } from '../../utility/localStorageControl';
import _ from 'lodash';
import { useNotification } from '../../context/notification.context';
import { useHistory } from 'react-router-dom';
import { Button } from '../../components/buttons/buttons';
import moment from 'moment-timezone';

function WaFormResponses({ match }) {
    const { apiClient } = useAuth();
    const history = useHistory();
    const flowUuid = match.params.uuid;
    const [searchText, setSearchText] = useState('');
    const { notifyError, notifySuccess, notifyWarning } = useNotification();
    const [exporting, setExporting] = useState(false);

    const [noOfResponses, setNoOfResponses] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSizeOptions = [10, 30, 50, 80, 120];

    const defaultSelectedColumns = ['contact', 'data'];
    const [openColNameSelector, setOpenColNameSelector] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState(
        getItem('wa-forms-response-columns') || defaultSelectedColumns,
    );
    const [selectedStatus, setSelectedStatus] = useState('ALL');
    const [selectedResponseIds, setSelectedResponseIds] = useState([]);
    const [markingAsComplete, setMarkingAsComplete] = useState(-1);

    const {
        data: formResponses,
        loading: loadingFormData,
        refetch: refetchFormData,
    } = useApiFetch(
        `/api/integrations/whatsapp/wa-flows/${flowUuid}/responses`,
        (data) => {
            setNoOfResponses(data.meta.total);
            return data.data;
        },
        true,
    );

    const {
        data: waForm,
        loading: loadingWaForm,
        refetch: refetchWaForm,
    } = useApiFetch(
        `/api/integrations/whatsapp/wa-flows?uuid=${flowUuid}`,
        (data) => {
            return data?.data?.[0];
        },
        false,
    );

    useEffect(() => {
        refetchFormData(
            `/api/integrations/whatsapp/wa-flows/${flowUuid}/responses?${qs.stringify({
                take: pageSize,
                skip: (currentPage - 1) * pageSize,
                completed: selectedStatus === 'COMPLETED' ? true : selectedStatus === 'IN COMPLETE' ? false : undefined,
                searchText: searchText?.trim() || undefined,
            })}`,
        );
    }, [pageSize, currentPage, searchText, selectedStatus]);

    const rowSelection = {
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedResponseIds(selectedRowKeys);
        },
    };

    const allColumns = useMemo(() => {
        const cols = [
            {
                title: 'Contact',
                dataIndex: 'contact',
                key: 'contact',
                align: 'left',
            },
        ];

        if (formResponses && formResponses[0]?.data) {
            const form = formResponses[0];
            const formResponse = form?.data;
            delete formResponse.flow_token;
            const formFields = Object.keys(formResponse);
            formFields.forEach((field) => {
                let title = field.split('-')[0];
                cols.push({
                    title: _.startCase(title),
                    dataIndex: field,
                    key: field,
                    align: 'left',
                });
            });
        }

        return [
            ...cols,
            {
                title: 'Actions',
                dataIndex: 'action',
                key: 'action',
                align: 'center',
                fixed: 'right',
            },
        ];
    }, [formResponses]);

    const tableData = useMemo(() => {
        if (!formResponses) {
            return [];
        }
        return (
            formResponses?.map((response) => {
                const { id, completed, contactUuid, contactName, contactPhone, data } = response;
                delete data?.flow_token;
                for (const key in data) {
                    if (Array.isArray(data[key])) {
                        data[key] = data[key].join(', ');
                    } else if (typeof data[key] === 'object') {
                        data[key] = JSON.stringify(data[key]);
                    } else if (typeof data[key] === 'boolean') {
                        data[key] = data[key] ? 'Yes' : 'No';
                    }
                }
                const row = {
                    key: id,
                    contact: `${contactName} (${contactPhone})`,
                    ...data,
                    action: completed ? (
                        <Button
                            type="primary"
                            size="medium"
                            color="primary"
                            outlined
                            icon={<FeatherIcon icon="check" size={14} />}
                            disabled
                        >
                            Completed
                        </Button>
                    ) : (
                        <>
                            <Button
                                type="primary"
                                size="medium"
                                color="primary"
                                outlined
                                loading={markingAsComplete === id}
                                icon={<FeatherIcon icon="check" size={14} />}
                                onClick={() => markAsComplete(id)}
                            >
                                Mark as Complete
                            </Button>
                        </>
                    ),
                };
                return row;
            }) || []
        );
    }, [formResponses]);

    const onShowSizeChange = async (current, pageSize) => {
        setPageSize(pageSize);
    };

    const exportResponses = async () => {
        try {
            setExporting(true);
            const response = await apiClient.get(`/api/integrations/whatsapp/wa-flows/${flowUuid}/responses/export`, {
                responseType: 'arraybuffer',
            });
            const blob = new Blob([response.data]);

            // const fileName = `${moment().format('YYYY-MM-DD HH:mm')}.csv`;
            const fileName = `${waForm.name} Responses - ${moment().format('YYYY-MM-DD HH:mm')}.csv`;
            const downloadLink = document.createElement('a');
            downloadLink.href = window.URL.createObjectURL(blob);
            downloadLink.download = fileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            notifySuccess({
                message: 'Responses exported',
            });
            setExporting(false);
        } catch (error) {
            console.error(error);
            message.error('Error while exporting!');
            setExporting(false);
        }
    };
    const markAsComplete = async (responseId) => {
        if (markingAsComplete !== -1) {
            return;
        }
        try {
            setMarkingAsComplete(responseId);
            await apiClient.put(`/api/integrations/whatsapp/wa-flows/${flowUuid}/responses/${responseId}`, {
                completed: true,
            });
            notifySuccess({
                message: 'Response marked as complete',
            });
            refetchFormData();
        } catch (error) {
            notifyError({
                message: 'Error while marking response as complete',
            });
        } finally {
            setMarkingAsComplete(-1);
        }
    };
    return (
        <>
            <CardToolbox>
                <PageHeader
                    routes={[
                        {
                            path: '/admin/overview',
                            icon: 'home',
                        },
                        {
                            breadcrumbName: 'Bot',
                        },
                        {
                            breadcrumbName: 'Whatsapp Forms',
                            path: '/admin/wa-forms',
                        },
                        {
                            breadcrumbName: waForm?.name || 'Loading...',
                            path: waForm ? `/admin/wa-forms/edit/${waForm.uuid}` : '#',
                        },
                        {
                            breadcrumbName: 'Responses',
                            path: window.location.pathname,
                        },
                    ]}
                    title={
                        <Space>
                            <FeatherIcon icon="list" size={16} />
                            {loadingWaForm ? <Spin></Spin> : waForm.name + ' Responses'}
                        </Space>
                    }
                    subTitle={
                        <>
                            <span className="title-counter">{noOfResponses} response(s) </span>
                            {/* <AutoComplete
                                onSearch={(value) => {
                                    setSearchText(value);
                                }}
                                // dataSource={notData}
                                placeholder="Name or Flow Id"
                                // width="200%"
                                patterns
                            /> */}
                        </>
                    }
                    buttons={
                        [
                            // <Button className="btn-add_new" size="default" type="primary" key="1">
                            //   <Link to="/admin/users/add-user/info">+ Add New User</Link>
                            // </Button>,
                            // <Link to="/admin/wa-forms/create">
                            //     <Button icon={<FeatherIcon icon="plus" size={14} />} type="primary">
                            //         Create Form
                            //     </Button>
                            // </Link>,
                        ]
                    }
                />
            </CardToolbox>

            <Main>
                <Cards
                    // title={
                    //     <Typography.Title level={5}>
                    //         <FeatherIcon icon="list" size={16} /> Forms
                    //     </Typography.Title>
                    // }
                    // headless
                    headerActions={
                        <Space direction="horizontal">
                            {selectedResponseIds.length > 0 && (
                                <Space>
                                    <Typography.Text>Selected {selectedResponseIds.length} contacts</Typography.Text>
                                </Space>
                            )}
                            <Button
                                onClick={exportResponses}
                                loading={exporting}
                                transparented
                                size="middle"
                                type="primary"
                                color="primary"
                                icon={!exporting && <FeatherIcon icon="download" size={14} />}
                            >
                                Export CSV
                            </Button>
                            <ProjectSorting>
                                <div className="project-sort-bar">
                                    <div className="project-sort-nav">
                                        <nav>
                                            <ul>
                                                {['ALL', 'COMPLETED', 'IN COMPLETE'].map((status) => (
                                                    <li
                                                        key={status}
                                                        className={selectedStatus === status ? 'active' : 'deactivate'}
                                                    >
                                                        <Link onClick={() => setSelectedStatus(status)} to="#">
                                                            {_.startCase(_.capitalize(status))}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            </ProjectSorting>
                            {/* <Button
                                icon={<FeatherIcon icon="refresh-cw" size={14} />}
                                type="primary"
                                outlined
                                loading={syncing}
                                onClick={syncForms}
                            >
                                Sync Status
                            </Button> */}
                            {/* <Button
                                type={'white'}
                                onClick={() => {
                                    setOpenColNameSelector(true);
                                }}
                            >
                                <FeatherIcon icon="columns" size={16} /> Customize Columns
                            </Button> */}
                        </Space>
                    }
                >
                    <>
                        <TableWrapper className="table-order table-responsive">
                            <Table
                                // rowSelection={rowSelection}
                                dataSource={tableData}
                                columns={allColumns}
                                loading={loadingFormData}
                                pagination={{
                                    pageSize: pageSize,
                                    // defaultCurrent: 1,
                                    current: currentPage,
                                    total: noOfResponses,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} contacts`,
                                    pageSizeOptions,
                                    onShowSizeChange: onShowSizeChange,
                                    showSizeChanger: true,
                                    onChange: (page, pageSize) => {
                                        if (page !== currentPage) {
                                            setCurrentPage(page);
                                        }
                                    },
                                }}
                            />
                        </TableWrapper>
                    </>
                </Cards>
            </Main>
            <TableColumnManager
                allColumns={allColumns}
                open={openColNameSelector}
                defaultSelected={defaultSelectedColumns}
                onSave={(selectedColumns) => {
                    setSelectedColumns(selectedColumns);
                    setOpenColNameSelector(false);
                }}
                saveKey={'wa-forms-response-columns'}
            />

            {/* <Drawer
                title="Contact Attributes"
                placement="right"
                closable={true}
                onClose={() => setOpenContactAttributes(false)}
                open={openContactAttributes}
                width={600}
            >
                <Spin spinning={loadingContactAttributes}>
                    <Row
                        gutter={[8, 16]}
                        style={{
                            paddingRight: '2rem',
                        }}
                    >
                        {contactAttributes?.map((attribute) => {
                            return (
                                <Col key={attribute.name} span={24}>
                                    <Row align={'middle'} gutter={[16, 16]}>
                                        <Col span={16}>
                                            <Input
                                                size="small"
                                                value={_.snakeCase(attribute.name).toUpperCase()}
                                                contentEditable={false}
                                            />
                                        </Col>
                                        <Col span={6}>
                                            <Select
                                                disabled={!attribute.editable}
                                                style={{ width: '100%' }}
                                                size="small"
                                                defaultValue={attribute.type}
                                                options={[
                                                    { label: 'Text', value: 'text' },
                                                    { label: 'Number', value: 'number' },
                                                    { label: 'Date', value: 'date' },
                                                    { label: 'Boolean', value: 'boolean' },
                                                ]}
                                            />
                                        </Col>
                                        <Col span={2}>
                                            <Button
                                                disabled={!attribute.deletable}
                                                size="medium"
                                                type={'white'}
                                                outlined
                                                shape={'round'}
                                                onClick={() => removeContactAttribute(attribute.id)}
                                                loading={removingAttribute === attribute.id}
                                                icon={<FeatherIcon icon="x" size={14} />}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            );
                        })}
                        {newAttribute && (
                            <Col span={24}>
                                <Row align={'middle'} gutter={[16, 16]}>
                                    <Col span={16}>
                                        <Input
                                            onChange={(e) => {
                                                setNewAttribute({
                                                    ...newAttribute,
                                                    name: e.target.value
                                                        .replace(/ /, '_')
                                                        .replace(/[^A-Za-z_]/g, '')
                                                        .toUpperCase(),
                                                });
                                            }}
                                            size="small"
                                            value={newAttribute.name}
                                            contentEditable={true}
                                        />
                                    </Col>
                                    <Col span={6}>
                                        <Select
                                            onChange={(value) => {
                                                setNewAttribute({ ...newAttribute, type: value });
                                            }}
                                            size="small"
                                            style={{ width: '100%' }}
                                            defaultValue={newAttribute.type}
                                            options={[
                                                { label: 'Text', value: 'text' },
                                                { label: 'Number', value: 'number' },
                                                { label: 'Date', value: 'date' },
                                                { label: 'Boolean', value: 'boolean' },
                                            ]}
                                        />
                                    </Col>
                                    <Col span={2}>
                                        <Button
                                            onClick={() => setNewAttribute(null)}
                                            size="medium"
                                            type={'white'}
                                            outlined
                                            shape={'round'}
                                            icon={<FeatherIcon icon="x" size={14} />}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        )}

                        {newAttribute ? (
                            <Button
                                onClick={addContactAttribute}
                                disabled={!newAttribute.name || !newAttribute.type}
                                type={'primary'}
                            >
                                Ok
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                outlined
                                onClick={() => {
                                    setNewAttribute({ name: '', type: 'text' });
                                }}
                                icon={<FeatherIcon icon="plus" size={14} />}
                            >
                                Add a new attribute
                            </Button>
                        )}
                    </Row>
                </Spin>
            </Drawer> */}
        </>
    );
}

export default WaFormResponses;
